/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Interpolation, css } from '@emotion/core';

type Styles = {
  mobile?: Interpolation;
  tablet?: Interpolation;
  desktop?: Interpolation;
};

// Avoid applying a `max-width` so that we can allow a cascading effect to
// happen, e.g. tablet styles will apply if desktop styles are not provided.
export const mediaQueries = {
  mobile: `@media (min-width: 0)`,
  tablet: `@media (min-width: 660px)`,
  desktop: `@media (min-width: 1024px)`,
};

export const getResponsiveStyles = (styles: Styles) => {
  return [
    { [mediaQueries.mobile]: css(styles.mobile) || {} },
    { [mediaQueries.tablet]: css(styles.tablet) || {} },
    { [mediaQueries.desktop]: css(styles.desktop) || {} },
  ];
};

// returns the responsive styles as a single object instead of an array
// useful for nested styles such as pseudo-elements like ::before
export const getResponsiveStylesObject = (styles: Styles) =>
  getResponsiveStyles(styles).reduce(
    (acc, style) => ({ ...acc, ...style }),
    {}
  );

export const visibleOnlyOnMobile = getResponsiveStyles({
  mobile: { display: 'inherit', visibility: 'visible' },
  tablet: { display: 'none', visibility: 'hidden' },
  desktop: { display: 'none', visibility: 'hidden' },
});

export const visibleOnlyOnTablet = getResponsiveStyles({
  mobile: { display: 'none', visibility: 'hidden' },
  tablet: { display: 'inherit', visibility: 'visible' },
  desktop: { display: 'none', visibility: 'hidden' },
});

export const visibleOnlyOnDesktop = getResponsiveStyles({
  mobile: { display: 'none', visibility: 'hidden' },
  tablet: { display: 'none', visibility: 'hidden' },
  desktop: { display: 'inherit', visibility: 'visible' },
});

export const hiddenOnMobile = getResponsiveStyles({
  mobile: { display: 'none', visibility: 'hidden' },
  tablet: { display: 'inherit', visibility: 'visible' },
  desktop: { display: 'inherit', visibility: 'visible' },
});

export const hiddenOnTabletAndMobile = getResponsiveStyles({
  mobile: { display: 'none', visibility: 'hidden' },
  tablet: { display: 'none', visibility: 'hidden' },
  desktop: { display: 'inherit', visibility: 'visible' },
});

export const hiddenOnDesktop = getResponsiveStyles({
  mobile: { display: 'inherit', visibility: 'visible' },
  tablet: { display: 'inherit', visibility: 'visible' },
  desktop: { display: 'none', visibility: 'hidden' },
});

export const VisibleOnlyOnMobile = ({
  children,
}: {
  children: React.ReactElement | React.ReactElement[];
}) => {
  if (process.env.NODE_ENV === 'test') {
    return null;
  }

  return (
    <div css={visibleOnlyOnMobile}>{children}</div>
  );
};
